import { useGtm } from '@gtm-support/vue-gtm';
import { nextTick } from 'vue';
import type { RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';
import axios from '@/axios';
import config from '@/config';
import eventBus from '@/event-bus';
import { devWarning } from '@/helpers/dev';
import { modal } from '@/helpers/ui';
import i18n from '@/i18n';
import { segment } from '@/segment';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useNativeAppStore } from '@/stores/native-app';
import { useSessionStore } from '@/stores/session';
import { useUserStore } from '@/stores/user';
import {
  isRouteAllowed,
  redirectRoute,
  redirectRouteWithoutParams,
} from './helpers';
import { waitForEl } from './helpers/internal';
import routes from './routes';

const router = createRouter({
  history: createWebHistory(),
  routes: routes as RouteRecordRaw[],
  scrollBehavior: async (to, _from, saved) => {
    if (saved) return saved;

    const highlight = to.query.highlight as string | undefined;
    if (highlight) {
      const safe =
        typeof CSS !== 'undefined' && CSS.escape
          ? CSS.escape(highlight)
          : highlight;
      const selector = `[data-highlight-id="${safe}"]`;
      const el = await waitForEl(selector, 2500); // Waits until the element is mounted or times out
      if (el) {
        setTimeout(() => {
          // Scroll the element into view
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Add a class which adds a highlight to the element
          el.classList.add('global-highlight');

          // Remove the class when the element is clicked
          el.addEventListener('click', () => {
            el.classList.remove('global-highlight');
          });
        }, 500); // Wait a little bit to scroll, to make it clearer for the user what is happening
        return false;
      }
    }

    return { left: 0, top: 0, behavior: 'auto' as ScrollBehavior };
  },
});

let localeFetched = false;
let firstRouteLoaded = false;

router.beforeEach(async (to, from) => {
  if (!firstRouteLoaded && to.name) {
    firstRouteLoaded = true;
    const sessionStore = useSessionStore();
    sessionStore.initialRoute = to;
  }

  if (to.params.locationId && typeof to.params.locationId === 'string') {
    // Save the locationId param from the route in the store
    const locationStore = useLocationsStore();
    locationStore.routeLocationId = to.params.locationId;
  }

  if (to.query.native === 'true') {
    const nativeAppStore = useNativeAppStore();
    nativeAppStore.isNativeApp = true;
  }

  const { requiredDataLoaded, fetchAllData } = useSessionStore();

  if (!requiredDataLoaded) {
    // Required data has not been loaded yet, fetch all data
    await fetchAllData();
  }

  const { user } = useUserStore();

  const { needsVerification } = useUserStore();
  if (needsVerification) {
    // User is logged in, but still needs to verify their account
    if (to.name !== 'verification') {
      return {
        name: 'verification',
      };
    } else {
      return;
    }
  } else if (to.name === 'verification') {
    // User is visiting the verification page, but does not need to verify

    return user
      ? redirectRoute({ query: to.query })
      : { name: 'login', query: to.query };
  }

  if (!user) {
    // User is not logged in

    let redirectToLogin = false;

    if (to.name === 'outside-app' || to.name === 'logged-out') {
      // User is visiting the root path, with or without a locale prefix (/, /en, /nl/).
      // Those only match the logged out layout, without any child route, so they would
      // render an empty page. Redirect to login page
      // Also redirect to login when visiting the logged out page, with no existing user

      redirectToLogin = true;
    } else if (to.name === 'two-factor-auth' && !from.name) {
      // Don't allow navigating to the two-factor-auth route directly, without coming from another route
      // Because it is dependend on data provided from the parent

      redirectToLogin = true;
    } else if (!isRouteAllowed(to)) {
      // When not logged in, the user is not allowed to visit any "logged in" routes. Redirect to login page in that case

      devWarning(
        'This user is not allowed to visit the requested route. Redirecting to login page.',
      );

      redirectToLogin = true;
    }

    if (redirectToLogin) {
      return {
        name: 'login',
        params: { locale: to.params.locale }, // keep the locale of the requested route
        query: to.query,
      };
    } else {
      // No redirect. Return here to not execute the other checks, which are only needed when the user is logged in
      return;
    }
  }

  const { isBlocked } = useCompanyStore();
  if (isBlocked) {
    // Company is blocked
    if (
      to.name !== 'admin-billing' &&
      to.name !== 'admin-billing-legacy' &&
      to.name !== 'logged-out'
    ) {
      return redirectRoute({ name: 'admin-billing' });
    }
    // should not redirect if already on admin-billing page
    return;
  }

  if (to.name === 'logged-out') {
    if (from.name) {
      // When logging out and navigating to the logged-out page, don't redirect
      // Only do this when actially logging out (when there is a from route), and still redirect when visiting the logged-out page directly
      return;
    } else {
      return redirectRoute({ name: 'dashboard', query: to.query });
    }
  }

  //  Redirect when user has trial expired
  const { isTrialExpired } = useCompanyStore();
  if (
    isTrialExpired &&
    !to.matched.find((route) => route.name === 'subscription') &&
    to.name !== 'logged-out'
  ) {
    return redirectRoute({ name: 'stripe-checkout' });
  }

  if (to.path === '/') {
    // User is visiting the root path. Redirect to dashboard

    return redirectRoute({ name: 'dashboard', query: to.query });
  }

  if (!isRouteAllowed(to)) {
    // User is not allowed to visit this route. Redirect to dashboard

    devWarning(
      'This user is not allowed to visit the requested route. Redirecting to dashboard page.',
    );
    return redirectRoute({ name: 'dashboard', query: to.query });
  }

  const { companyId } = useCompanyStore();
  const { locationId } = useLocationsStore();

  if (to.name === '404' || to.name === 'outside-app') {
    // Instead of showing a 404 page, we redirect the user based on the requested url
    // When company or location params are missing, we redirect them to the correct page
    // For example, visiting /calendar or /c/1/calendar should both redirect to /c/l/1/calendar
    // We do this by matching the last segment of the route path with existing routes

    const paths = to.path.split('/').filter((path) => !!path);
    if (companyId && locationId) {
      const routeObj = redirectRouteWithoutParams(paths, {
        companyId,
        locationId,
      });
      if (routeObj?.name) {
        devWarning(
          'This user did not specify both companyId and locationId in the path, redirecting with those params',
        );
        return redirectRoute({
          name: routeObj.name.toString(),
          params: routeObj.params,
          query: to.query,
        });
      }
    }
  }

  if (
    to.name &&
    to.name !== 'become' &&
    (to.params.companyId !== companyId?.toString() ||
      to.params.locationId !== locationId?.toString())
  ) {
    if (user.salonizedAdmin && to.params.companyId) {
      return {
        name: 'become',
      };
    } else {
      // User is visiting a correct and allowed route, but it using the wrong companyId or locationId
      // Redirect to the same route with the correct params
      devWarning(
        'This user is not using the correct companyId and locationId. Redirecting',
      );
      return redirectRoute({ name: to.name.toString(), query: to.query });
    }
  }
});

router.afterEach((to, from) => {
  const { company, companyNeedsType } = useCompanyStore();
  const { user } = useUserStore();

  // Company needs to select type, show the type selection modal
  if (companyNeedsType) {
    nextTick(() => modal('company-type'));
  }

  // Company is from Wavy, user needs to set a password, show the password set modal
  if (company.fromWavy && user?.mustSetPassword) {
    nextTick(() => modal('wavy-onboarding'));
  }

  // Track pageview with Google Tag Manager
  const gtm = useGtm();
  if (gtm) {
    gtm.trackEvent({
      event: 'pageview',
      route: to.name,
      url: to.path,
    });
  }

  // Track the page view with Segment, but only when the route name changes, and the new route has a name
  if (to.name && to.name !== from.name) {
    segment.page(to.name as string, {
      page_name: to.name as string,
    });
  }

  // When there is no company data and the route does not have a locale param, fetch the locale from the backend
  if (!company && !to.params.locale && !localeFetched && !config.isTest) {
    axios.get('/sessions/visitor_locale').then(({ data: { locale } }) => {
      i18n.locale.value = locale?.default_language || 'en';
      localeFetched = true;
    });
  }

  // When the route has a locale param, set the i18n locale
  const locale = to.params.locale?.toString();
  if (locale && i18n.availableLocales?.includes(locale)) {
    // Only set the locale if the locale from the route param actually exists in i18n
    i18n.locale.value = locale;
  } else if (locale && to.name) {
    // The route has a locale param, but the locale doesn't exist in the available locales of i18n
    // Replace the route without the param

    router.replace({
      name: to.name,
      params: {
        locale: null,
      },
    });
  }
});

eventBus.$on('logout', () => {
  router.push({ name: 'logged-out' }).then(() => {
    const { resetData } = useSessionStore();
    resetData();
  });
});

export default router;
